import React, { useEffect, useRef, useState } from 'react';
import logo from '../assets/Logo_with_word.png';

const API_KEY = process.env.REACT_APP_YOUTUBE_API;

function YouTubeAudioPlayer() {
  const playerRef = useRef(null);
  const [videoId, setVideoId] = useState('');
  const [isPlayerReady, setIsPlayerReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [volume, setVolume] = useState(100);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [playOrder, setPlayOrder] = useState([]);
  const [shuffle, setShuffle] = useState(false);
  const [repeat, setRepeat] = useState(false);

  // Create refs for the latest state values
  const shuffleRef = useRef(false);
  const repeatRef = useRef(false);
  const currentIndexRef = useRef(-1);
  const playOrderRef = useRef([]);
  const resultsRef = useRef([]);

  // Keep refs updated with state
  useEffect(() => { shuffleRef.current = shuffle; }, [shuffle]);
  useEffect(() => { repeatRef.current = repeat; }, [repeat]);
  useEffect(() => { currentIndexRef.current = currentIndex; }, [currentIndex]);
  useEffect(() => { playOrderRef.current = playOrder; }, [playOrder]);
  useEffect(() => { resultsRef.current = results; }, [results]);

  // Get current thumbnail URL
  const getCurrentThumbnail = () => {
    if (videoId) {
      return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
    }
    return null;
  };

  // Generate play order
  const generatePlayOrder = (length, isShuffled, currentIdx = -1) => {
    if (length === 0) return [];
    
    const indices = Array.from({ length }, (_, i) => i);
    
    if (isShuffled) {
      // Fisher-Yates shuffle
      const shuffled = [...indices];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      
      // If we have a current song, try to put it first in the shuffled order
      if (currentIdx >= 0 && currentIdx < length) {
        const currentInShuffled = shuffled.indexOf(currentIdx);
        if (currentInShuffled > 0) {
          [shuffled[0], shuffled[currentInShuffled]] = [shuffled[currentInShuffled], shuffled[0]];
        }
      }
      
      return shuffled;
    } else {
      return indices;
    }
  };

  // Update play order when results change
  useEffect(() => {
    if (results.length > 0) {
      const newPlayOrder = generatePlayOrder(results.length, shuffle, currentIndex);
      setPlayOrder(newPlayOrder);
    } else {
      setPlayOrder([]);
    }
  }, [results.length]); // Only depend on results.length, not shuffle

  // Handle shuffle toggle
  const toggleShuffle = () => {
    const newShuffle = !shuffle;
    setShuffle(newShuffle);
    
    if (results.length > 0) {
      const newPlayOrder = generatePlayOrder(results.length, newShuffle, currentIndex);
      setPlayOrder(newPlayOrder);
    }
  };

  // Update current index when video changes
  useEffect(() => {
    if (videoId && results.length > 0) {
      const index = results.findIndex(item => item.id.videoId === videoId);
      setCurrentIndex(index);
    }
  }, [videoId, results]);

  // Internet status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Load YouTube API
  useEffect(() => {
    if (!window.YT) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      document.body.appendChild(tag);
    } else {
      setIsPlayerReady(true);
    }
    window.onYouTubeIframeAPIReady = () => setIsPlayerReady(true);
  }, []);

  // Initialize player only once
  useEffect(() => {
    if (isPlayerReady && !playerRef.current) {
      playerRef.current = new window.YT.Player('player', {
        height: '0',
        width: '0',
        events: {
          onReady: () => {
            playerRef.current.setVolume(volume);
          },
          onStateChange: handlePlayerStateChange,
        },
      });
    }
  }, [isPlayerReady]);

  // Load video when videoId changes
  useEffect(() => {
    if (isPlayerReady && playerRef.current && videoId) {
      playerRef.current.loadVideoById(videoId);
      setIsPlaying(true);
    }
  }, [videoId, isPlayerReady]);

  // Time update interval
  useEffect(() => {
    let interval;
    if (isPlaying) {
      interval = setInterval(() => {
        if (playerRef.current && playerRef.current.getCurrentTime) {
          setCurrentTime(playerRef.current.getCurrentTime());
          setDuration(playerRef.current.getDuration());
        }
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isPlaying]);

  // Player state change
  const handlePlayerStateChange = (event) => {
    if (event.data === window.YT.PlayerState.PLAYING) {
      setIsPlaying(true);
      setDuration(playerRef.current.getDuration());
    } else if (event.data === window.YT.PlayerState.PAUSED) {
      setIsPlaying(false);
    } else if (event.data === window.YT.PlayerState.ENDED) {
      if (repeatRef.current) {
        // Repeat current song
        playerRef.current.seekTo(0);
        playerRef.current.playVideo();
      } else {
        // Auto-play next song
        autoplayNext();
      }
    }
  };

  // Play next song
  const playNext = () => {
    const currentResults = resultsRef.current;
    const currentPlayOrder = playOrderRef.current;
    const currentIdx = currentIndexRef.current;
    
    if (currentResults.length === 0 || currentPlayOrder.length === 0) return;
    
    // Find current position in play order
    const currentPlayOrderIndex = currentPlayOrder.indexOf(currentIdx);
    
    if (currentPlayOrderIndex === -1) {
      // If current song is not in play order, start from beginning
      const nextIndex = currentPlayOrder[0];
      if (nextIndex < currentResults.length) {
        setVideoId(currentResults[nextIndex].id.videoId);
      }
      return;
    }
    
    // Get next song in play order
    const nextPlayOrderIndex = (currentPlayOrderIndex + 1) % currentPlayOrder.length;
    const nextIndex = currentPlayOrder[nextPlayOrderIndex];
    
    if (nextIndex < currentResults.length) {
      setVideoId(currentResults[nextIndex].id.videoId);
    }
  };

  // Play previous song
  const playPrevious = () => {
    const currentResults = resultsRef.current;
    const currentPlayOrder = playOrderRef.current;
    const currentIdx = currentIndexRef.current;
    
    if (currentResults.length === 0 || currentPlayOrder.length === 0) return;
    
    // Find current position in play order
    const currentPlayOrderIndex = currentPlayOrder.indexOf(currentIdx);
    
    if (currentPlayOrderIndex === -1) {
      // If current song is not in play order, start from end
      const prevIndex = currentPlayOrder[currentPlayOrder.length - 1];
      if (prevIndex < currentResults.length) {
        setVideoId(currentResults[prevIndex].id.videoId);
      }
      return;
    }
    
    // Get previous song in play order
    const prevPlayOrderIndex = currentPlayOrderIndex === 0 
      ? currentPlayOrder.length - 1 
      : currentPlayOrderIndex - 1;
    const prevIndex = currentPlayOrder[prevPlayOrderIndex];
    
    if (prevIndex < currentResults.length) {
      setVideoId(currentResults[prevIndex].id.videoId);
    }
  };

  // Autoplay next (for when a song ends)
  const autoplayNext = () => {
    playNext();
  };

  // Toggle repeat
  const toggleRepeat = () => {
    setRepeat(!repeat);
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) return;
    setLoading(true);
    try {
      const res = await fetch(
        `https://www.googleapis.com/youtube/v3/search?part=snippet&q=${encodeURIComponent(
          searchTerm
        )}&type=video&key=${API_KEY}&maxResults=10`
      );
      const data = await res.json();
      setLoading(false);
      if (data.items) {
        setResults(data.items);
        setCurrentIndex(-1); // Reset current index when new search results come in
      }
    } catch (error) {
      console.error('Search error:', error);
      setLoading(false);
    }
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60).toString().padStart(2, '0');
    return `${minutes}:${seconds}`;
  };

  const currentThumbnail = getCurrentThumbnail();

  return (
    <div className="min-h-screen w-full relative flex items-center justify-center p-4 md:p-8 overflow-x-hidden overflow-y-auto">
      <style jsx>{`
        .slider-white::-webkit-slider-thumb {
          appearance: none;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #ffffff;
          cursor: pointer;
          border: 2px solid #ffffff;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }
        .slider-white::-moz-range-thumb {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #ffffff;
          cursor: pointer;
          border: 2px solid #ffffff;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }
      `}</style>
      {/* Dynamic Background */}
      <div className="absolute inset-0 z-0">
        {currentThumbnail ? (
          <>
            {/* Background Image */}
            <div 
              className="absolute inset-0 bg-cover bg-center bg-no-repeat transition-all duration-1000 ease-in-out"
              style={{
                backgroundImage: `url(${currentThumbnail})`,
                filter: 'blur(20px)',
                //transform: 'scale(1.1)', // Slightly scale up to avoid blur edges
              }}
            />
            {/* Overlay for better readability */}
            <div className="absolute inset-0 bg-black bg-opacity-60 transition-opacity duration-1000 ease-in-out" />
          </>
        ) : (
          /* Fallback gradient background */
          <div className="absolute inset-0 bg-gradient-to-b from-black via-gray-800 to-gray-900" />
        )}
      </div>

      {/* Logo positioned absolutely at top left */}
      <div className="absolute top-4 left-4 z-10">
        <img 
          src={logo} 
          alt="Music Logo" 
          className="h-8 md:h-10 w-auto opacity-90 hover:opacity-100 transition-opacity cursor-pointer"
          onError={(e) => {
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'block';
          }}
        />
        <div 
          className="h-8 md:h-10 px-3 md:px-4 bg-green-500 rounded-lg flex items-center justify-center text-white font-bold text-xs md:text-sm opacity-90 hover:opacity-100 transition-opacity cursor-pointer"
          style={{ display: 'none' }}
        >
          MUSIC
        </div>
      </div>

      {/* Main Content */}
      <div className="w-full max-w-6xl flex flex-col md:flex-row gap-6 md:gap-12 scale-100 md:scale-100 relative z-10">
        {/* Left Panel */}
        <div className="bg-white bg-opacity-10 backdrop-blur-lg rounded-xl p-4 md:p-8 flex-1 flex flex-col shadow-lg border border-white border-opacity-20 min-w-0" style={{ height: '700px', minWidth: '0', maxWidth: '100%' }}>
          <div className="mb-4 md:mb-6">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Search for music..."
              className="w-full px-4 md:px-6 py-3 md:py-4 rounded-full bg-white text-black placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50 text-base md:text-lg"
            />
          </div>

          <div className="flex-1 overflow-y-auto min-h-0">
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <p className="text-white text-lg">Loading...</p>
              </div>
            ) : results.length === 0 ? (
              <div className="flex items-center justify-center h-32">
                <p className="text-gray-400 text-lg">Search for music to get started</p>
              </div>
            ) : (
              results.map((item, index) => (
                <div
                  key={item.id.videoId}
                  onClick={() => setVideoId(item.id.videoId)}
                  className={`flex items-center bg-gray-700 bg-opacity-50 rounded-lg p-3 mb-3 cursor-pointer hover:bg-gray-600 transition ${
                    index === currentIndex ? 'bg-white-600' : ''
                  }`}
                >
                  <img
                    src={item.snippet.thumbnails.default.url}
                    alt={item.snippet.title}
                    className="w-20 h-20 rounded mr-6 object-cover"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium text-base truncate">{item.snippet.title}</p>
                    <p className="text-gray-400 text-sm truncate">{item.snippet.channelTitle}</p>
                  </div>
                  {index === currentIndex && (
                    <div className="w-4 h-4 bg-white rounded-full ml-4 flex-shrink-0"></div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Right Panel */}
        <div className="bg-white bg-opacity-10 backdrop-blur-lg rounded-xl p-4 md:p-8 flex-1 flex flex-col items-center text-center text-white relative shadow-lg border border-white border-opacity-20 min-w-0" style={{ height: '700px', minWidth: '0', maxWidth: '100%' }}>
          {/* Thumbnail */}
          <div className="mb-4 md:mb-6">
            {videoId ? (
              <img
                src={`https://img.youtube.com/vi/${videoId}/hqdefault.jpg`}
                alt="Thumbnail"
                className="rounded-xl w-80 h-80 object-cover shadow-lg"
              />
            ) : (
              <div className="w-80 h-80 bg-gray-700 rounded-xl flex items-center justify-center">
                <div className="text-center">
                  <div className="w-20 h-20 bg-gray-600 rounded-full mx-auto mb-4 flex items-center justify-center">
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="currentColor" className="text-gray-400">
                      <path d="M8 5v14l11-7z"/>
                    </svg>
                  </div>
                  <p className="text-gray-400">No song selected</p>
                </div>
              </div>
            )}
          </div>

          {/* Title */}
          <h3 className="text-lg md:text-xl font-semibold mb-1 md:mb-2 max-w-md truncate">
            {results.find(item => item.id.videoId === videoId)?.snippet.title || "No song selected"}
          </h3>
          <p className="text-gray-400 mb-4 md:mb-6 text-base md:text-lg truncate">
            {results.find(item => item.id.videoId === videoId)?.snippet.channelTitle || ""}
          </p>

          {/* Seekbar */}
          <div className="flex items-center gap-2 md:gap-4 w-full max-w-md mb-4 md:mb-6 scale-100">
            <span className="text-sm">{formatTime(currentTime)}</span>
            <input
              type="range"
              min="0"
              max={duration || 0}
              value={currentTime}
              onChange={(e) => {
                if (playerRef.current && playerRef.current.seekTo) {
                  const seekTo = parseInt(e.target.value);
                  playerRef.current.seekTo(seekTo, true);
                  setCurrentTime(seekTo);
                }
              }}
              className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider-white"
              style={{
                background: `linear-gradient(to right, #ffffff 0%, #ffffff ${((currentTime / duration) * 100) || 0}%, #374151 ${((currentTime / duration) * 100) || 0}%, #374151 100%)`
              }}
            />
            <span className="text-sm">{formatTime(duration)}</span>
          </div>

          {/* Controls */}
          <div className="flex items-center justify-center gap-4 md:gap-8 mb-6 md:mb-10 flex-wrap">
            <button
              onClick={toggleShuffle}
              className={`w-12 h-12 flex items-center justify-center rounded-full transition ${shuffle ? 'text-white bg-white bg-opacity-20' : 'text-gray-300 hover:text-white'}`}
              title={shuffle ? 'Shuffle: On' : 'Shuffle: Off'}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                <path d="M7.5 4.21l4.5 2.6 4.5-2.6"/>
                <path d="M7.5 19.79V14.6L3 12"/>
                <path d="M21 12l-4.5 2.6v5.19"/>
                <path d="M12 6.81v10.38"/>
                <circle cx="12" cy="12" r="2"/>
              </svg>
            </button>
            <button 
              onClick={playPrevious}
              className="w-12 h-12 flex items-center justify-center rounded-full text-gray-300 hover:text-white transition"
              title="Previous"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 20L9 12l10-8v16z"/>
                <line x1="5" y1="19" x2="5" y2="5"/>
              </svg>
            </button>
            {isPlaying ? (
              <button
                onClick={() => {
                  if (playerRef.current) {
                    playerRef.current.pauseVideo();
                  }
                }}
                className="w-16 h-16 flex items-center justify-center rounded-full bg-white hover:bg-gray-100 transition"
                title="Pause"
              >
                <svg width="32" height="32" viewBox="0 0 24 24" fill="black">
                  <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
                </svg>
              </button>
            ) : (
              <button
                onClick={() => {
                  if (playerRef.current) {
                    playerRef.current.playVideo();
                  }
                }}
                className="w-16 h-16 flex items-center justify-center rounded-full bg-white hover:bg-gray-100 transition"
                title="Play"
              >
                <svg width="32" height="32" viewBox="0 0 24 24" fill="black">
                  <path d="M8 5v14l11-7z"/>
                </svg>
              </button>
            )}
            <button 
              onClick={playNext}
              className="w-12 h-12 flex items-center justify-center rounded-full text-gray-300 hover:text-white transition"
              title="Next"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 4l10 8-10 8V4z"/>
                <line x1="19" y1="5" x2="19" y2="19"/>
              </svg>
            </button>
            <button
              onClick={toggleRepeat}
              className={`w-12 h-12 flex items-center justify-center rounded-full transition ${repeat ? 'text-white bg-white bg-opacity-20' : 'text-gray-300 hover:text-white'}`}
              title={repeat ? 'Repeat: On' : 'Repeat: Off'}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 1l4 4-4 4"/>
                <path d="M3 11V9a4 4 0 0 1 4-4h14"/>
                <path d="M7 23l-4-4 4-4"/>
                <path d="M21 13v2a4 4 0 0 1-4 4H3"/>
              </svg>
            </button>
          </div>

          {/* Volume */}
          <div className="flex items-center gap-2 md:gap-4 w-full max-w-xs scale-100 mt-2 md:mt-4">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-white">
              <path d="M11 5L6 9H2v6h4l5 4V5z"/>
              <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"/>
            </svg>
            <input
              type="range"
              min="0"
              max="100"
              value={volume}
              onChange={(e) => {
                const newVolume = parseInt(e.target.value);
                setVolume(newVolume);
                if (playerRef.current && playerRef.current.setVolume) {
                  playerRef.current.setVolume(newVolume);
                }
              }}
              className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider-white"
              style={{
                background: `linear-gradient(to right, #ffffff 0%, #ffffff ${volume}%, #374151 ${volume}%, #374151 100%)`
              }}
            />
            <span className="text-sm w-8 text-right">{volume}</span>
          </div>

          {/* Status indicators */}
          <div className="absolute bottom-2 md:bottom-4 left-2 md:left-4 flex items-center gap-2 md:gap-4">
            {/*<div className="flex items-center gap-2">
              <span className={`h-3 w-3 rounded-full ${isOnline ? 'bg-green-500' : 'bg-red-500'}`} />
              <p className="text-sm">{isOnline ? 'Online' : 'Offline'}</p>
            </div> */}
            {shuffle && (
              <div className="flex items-center gap-2">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-white">
                  <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                  <circle cx="12" cy="12" r="2"/>
                </svg>
                <p className="text-sm text-white">Shuffle</p>
              </div>
            )}
            {repeat && (
              <div className="flex items-center gap-2">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-white">
                  <path d="M17 1l4 4-4 4"/>
                  <path d="M3 11V9a4 4 0 0 1 4-4h14"/>
                  <path d="M7 23l-4-4 4-4"/>
                  <path d="M21 13v2a4 4 0 0 1-4 4H3"/>
                </svg>
                <p className="text-sm text-white">Repeat</p>
              </div>
            )}
          </div>

          {/* Hidden Player */}
          <div id="player-container" className="hidden">
            <div id="player" />
          </div>
        </div>
      </div>
    </div>
  );
}

export default YouTubeAudioPlayer;